<script setup lang="ts">
import type { Event, ProductDetails } from "mws-ts-v2-sdk";

import { eventNameClicked, logoPressed } from "~/analytics/segment";

type Props = {
  product: ProductDetails;
  event: Event;
};

const { product, event } = defineProps<Props>();

const localePath = useLocalePath();

const { featuredTeam } = useEventDetails(() => event);

const trackingParams = computed(() => {
  return {
    event: {
      id: event.id,
      name: event.name,
    },
    product: {
      id: product.id,
      name: product.name,
    },
    featuredTeam: featuredTeam.value
      ? { id: featuredTeam.value.participantId, name: featuredTeam.value.name }
      : undefined,
  };
});

const onEventNameClicked = () => {
  eventNameClicked(trackingParams.value);
};

const onLogoClicked = () => {
  logoPressed(trackingParams.value);
};

const { setTeamName } = useTeamContext();

watchEffect(() => {
  if (featuredTeam.value) {
    setTeamName(featuredTeam.value.name);
  }
});
</script>

<template>
  <div class="flex flex-col py-4 md:py-0 md:flex-col">
    <div class="flex flex-row gap-1 justify-between content-center">
      <div>
        <h1 class="text-3xl uppercase">
          {{ product.name }}
        </h1>
        <!-- Mobile view highlights -->
        <div
          v-if="product.highlights.length > 0"
          class="py-3 md:hidden flex flex-row gap-1"
        >
          <BaseHighlight
            v-for="highlight in product.highlights"
            :key="highlight.id"
            :icon-url="highlight.logoUrl"
            :highlight="highlight.name"
          />
        </div>

        <NuxtLink
          data-cy="product-event-link"
          :to="localePath(getEventRoute(event))"
          @click="onEventNameClicked"
        >
          <EventName
            :event="event"
            direction="horizontal"
            :divider="true"
            class="text-platform-text-2 gap-1 uppercase text-sm md:text-md decoration-gray-300 underline decoration-dashed underline-offset-4"
          />
        </NuxtLink>
      </div>

      <NuxtLink
        :to="localePath(getEventRoute(event))"
        @click="onLogoClicked"
      >
        <NuxtImg
          provider="imgix"
          preset="logoMedium"
          :alt="`${event.name} logo`"
          :src="event.logoUrl || logoFallbackPath"
          class="w-16 h-16 object-contain"
        />
      </NuxtLink>
    </div>
    <!-- Desktop view highlights -->
    <div
      v-if="product.highlights.length > 0"
      class="pt-4 hidden md:flex flex-row gap-1"
    >
      <BaseHighlight
        v-for="highlight in product.highlights"
        :key="highlight.id"
        :icon-url="highlight.logoUrl"
        :highlight="highlight.name"
      />
    </div>
  </div>
</template>
